import { Anchor, Badge, Button, Group, Kbd, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconAlertCircle,
  IconBan,
  IconCalendar,
  IconCheck,
  IconCircleCheck,
  IconFileAlert,
  IconLogout,
  IconPencil,
  IconPercentage,
  IconPlayerPause,
  IconPlayerPlay,
  IconSend,
  IconSquareCheck,
  IconStack2,
  IconTrash,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import CustomVariablesBatchForm from 'app/Private/CustomVariables/CustomVariablesBatchForm/CustomVariablesBatchForm';
import { DisputeReasonForm } from 'app/Private/Reasons/DisputeReasonForm/DisputeReasonForm';
import { PauseReasonForm } from 'app/Private/Reasons/PauseReasonForm/PauseReasonForm';
import { UnrecoverableReasonForm } from 'app/Private/Reasons/UnrecoverableReasonForm/UnrecoverableReasonForm';
import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { isNil, omit, reject } from 'lodash-es';
import { useLoadRecoveryPlans } from 'modules/Settings/Workflows/api';
import { TaskForm, type TaskFormProps } from 'modules/Tasks/TaskForm/TaskForm';
import posthog from 'posthog-js';
import qs from 'query-string';
import { type KeyboardEvent, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import MoveRecoveryPlanInvoices from 'shared/action-component/MoveRecoveryPlanInvoices';
import { captureEvent } from 'shared/analytics/capture-event';
import ChooseCollaboratorModal from 'shared/components/ChooseCollaboratorModal';
import ChooseRecoveryPlanModal from 'shared/components/ChooseRecoveryPlanModal';
import ListResModal from 'shared/components/DeleteModal';
import { AmlModal } from 'shared/components/InvoiceActionsDropdown/AmlModal';
import { ChooseThirdPartyTypeForm } from 'shared/components/InvoiceActionsDropdown/ChooseThirdPartyTypeForm';
import FormalNoticeForm from 'shared/components/InvoiceActionsDropdown/FormalNotice/FormalNoticeForm';
import { GcollectModal } from 'shared/components/InvoiceActionsDropdown/GcollectModal';
import { LegalCityModal } from 'shared/components/InvoiceActionsDropdown/LegalCityModal/LegalCityModal';
import SelectThirdPartyForm from 'shared/components/InvoiceActionsDropdown/SelectThirdPartyForm';
import MoveRecoveryPlanInvoiceModal from 'shared/components/MoveRecoveryPlanInvoiceModal';
import type { ThirdParties, ThirdPartyType } from 'shared/hooks/use-load-third-parties';
import { error } from 'shared/notifications';
import { treatRecoveryPlan } from 'shared/serializer';
import { useCanUseManualActions } from 'shared/utils/connector';
import { useRefresh } from 'shared/utils/hooks';
import { queryParamParser } from 'shared/utils/view';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { settingsActions } from 'store/settings/settings.actions';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { Invoice } from 'types/store/invoice-state';
import SendingOptionsForm from '../Invoice/SendingOptionsForm';
import styleIdentifiers from './BatchActions.module.scss';
import { type BatchAction, BatchActionsList, Categories } from './BatchActionsList';
import { buildBatchActionProperties } from './batch-action-analytics';

const styles = classNames.bind(styleIdentifiers);

export type BatchActionsProps = {
  selectedInvoices: Invoice[];
  isSelectedAll: boolean;
  totalObjects: number;
  reload?: VoidFunction;
};

const BATCH_ACTIONS_MODAL_ID = 'batch-actions';

export const openBatchActionsModal = ({
  title,
  closeLabel,
  ...batchActionsProps
}: BatchActionsProps & { title: string; closeLabel: string }) => {
  modals.open({
    modalId: BATCH_ACTIONS_MODAL_ID,
    title,
    size: 780,
    radius: 18,
    centered: false,
    yOffset: 48,
    closeButtonProps: { 'aria-label': closeLabel },
    styles: {
      header: { borderBottom: 'none', paddingBottom: 0 },
      content: { display: 'flex', flexDirection: 'column' },
      body: { display: 'flex', flexDirection: 'column', minHeight: 0 },
    },
    children: <BatchActions {...batchActionsProps} />,
  });
};

export default function BatchActions(batchActionsProps: BatchActionsProps) {
  const {
    selectedInvoices,
    isSelectedAll: _isSelectedAll,
    totalObjects,
    reload,
  } = batchActionsProps;
  const { t } = useTranslation();
  const history = useHistory();

  const company = useCompanyConfiguration();
  const hasManualActionsActive = useCanUseManualActions();

  const [isSelectedAll, setSelectedAll] = useState<boolean>(_isSelectedAll);

  const selectedCount = isSelectedAll ? totalObjects : selectedInvoices.length;

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const selectWholeView = () => {
    setSelectedAll(true);
    searchFieldRef.current?.focus();
    modals.updateModal({
      modalId: BATCH_ACTIONS_MODAL_ID,
      children: <BatchActions {...batchActionsProps} isSelectedAll />,
    });
  };

  const { doRefresh } = useRefresh();
  const { recoveryPlans = [] } = useLoadRecoveryPlans();

  if (company == null) return null;

  const aysModal = (action_type: any) => (onSubmit: any) => {
    modals.openConfirmModal({
      size: 'sm',
      title: t(i18nKeys.CONFIRMATION),
      children: (
        <Text size="sm">
          {t(
            selectedInvoices.length === 1 && !isSelectedAll
              ? tKeysLoose.AYS[`INVOICE_ACTION_${action_type.toUpperCase()}`]
              : tKeysLoose.AYS[`INVOICES_ACTION_${action_type.toUpperCase()}`],
            { count: selectedCount },
          )}
        </Text>
      ),
      labels: { confirm: t(i18nKeys.CONFIRM), cancel: t(i18nKeys.CANCEL) },
      confirmProps: {
        autoFocus: true,
        onKeyDown: (event: KeyboardEvent) => {
          if (event.repeat) event.preventDefault();
        },
      },
      onConfirm: () => onSubmit({}),
    });
  };

  const batchAction = (action_type: string, action: any) => () => {
    const data = {} as any;

    if (isSelectedAll) {
      data.filter_params = queryParamParser(qs.parse(location.search));
      data.view_id = location.hash.slice(1);
    } else data.invoice_ids = selectedInvoices.map(invoice => invoice.id);

    return action(
      (values: any) =>
        new Promise<void>(resolve => {
          captureEvent(
            posthog,
            'views:batch_action_executed',
            buildBatchActionProperties({
              actionType: action_type,
              isSelectedAll,
              selectedCount: selectedInvoices.length,
              totalObjects,
              page: location.pathname,
            }),
          );
          invoiceActions.actionsBatchAction({
            data: {
              ...data,
              ...values,
              action_type,
            },
            callback: (res: any) => {
              modals.closeAll();
              showDialog({
                id: DialogShowId.CUSTOM,
                size: DialogShowSize.MEDIUM,
                title: t(i18nKeys.RESULT),
                children: <ListResModal data={res} />,
              });
              doRefresh();
              reload?.();
              resolve();
            },
            actionFailure: () => resolve(),
          });
        }),
    );
  };

  const paymentPlan = () => {
    const data = {} as any;
    if (isSelectedAll) {
      data.filter_params = queryParamParser(qs.parse(location.search));
      data.view_id = location.hash.slice(1);
    } else {
      data.invoice_ids = selectedInvoices.map(invoice => invoice.id);
    }

    invoiceActions.actionsBatchAction({
      data: {
        ...data,
        action_type: 'payment_plan',
      },
      callback: (response: any) => {
        const { debtor_id, invoice_ids } = response[0];
        history.push(`/payment-plans/new?debtor_id=${debtor_id}&invoice_ids=${invoice_ids}`);
        modals.closeAll();
      },
    });
  };

  const pause = () => {
    modals.open({
      size: 'sm',
      title: t(i18nKeys.CLIENT.PAUSE),
      children: (
        <PauseReasonForm
          onSubmit={values => batchAction('pause', (submit: any) => submit(values))()}
        />
      ),
    });
  };

  const dispute = () => {
    modals.open({
      size: 'sm',
      title: t('INVOICE.DISPUTED'),
      children: (
        <DisputeReasonForm
          onSubmit={values => batchAction('dispute', (submit: any) => submit(values))()}
        />
      ),
    });
  };

  const markAsUnrecoverable = () => {
    modals.open({
      size: 'sm',
      title: t('INVOICE.UNRECOVERABLE'),
      children: (
        <UnrecoverableReasonForm
          onSubmit={values =>
            batchAction('mark_as_unrecoverable', (submit: any) => submit(values))()
          }
        />
      ),
    });
  };

  const selectStepPlan = (
    newPlan: any,
    plans: any,
    counter: any,
    maxItems: any,
    callback: any,
    step_switchs: any[] = [],
  ) => {
    MoveRecoveryPlanInvoices({
      newPlan,
      oldPlan: plans[counter - 1],
      title: t(i18nKeys.PLAN.MOVE_INVOICES),
      onSubmit: (steps_switch: any) => {
        step_switchs.push({
          to: newPlan.id,
          from: plans[counter - 1].id,
          steps_switch,
        });
        if (counter === maxItems) {
          callback(step_switchs);
        } else {
          selectStepPlan(newPlan, plans, counter + 1, maxItems, callback, step_switchs);
        }
      },
    });
  };

  const movePlan = async () => {
    let scope = {
      scope: 'invoice',
    } as any;
    if (isSelectedAll) {
      scope = { scope: 'invoice', ...qs.parse(location.search) };
      scope.view_id = location.hash.slice(1);
    } else {
      scope.invoice_ids = selectedInvoices.map(invoice => invoice.id);
    }

    const planId: string | null = await new Promise(resolve => {
      modals.open({
        size: 'md',
        title: t(i18nKeys.PLAN.WHERE_MOVE_INVOICES),
        children: (
          <ChooseRecoveryPlanModal
            recoverPlans={recoveryPlans}
            onClick={selectedPlan => resolve(selectedPlan ?? null)}
          />
        ),
      });
    });

    modals.closeAll();

    const response: any = await new Promise(resolve => {
      settingsActions.getRecoveryPlan({
        id: Number(planId),
        callback: resolve,
      });
    });

    const { newPlan, out_of_plan, ...res } = await new Promise<any>(resolve => {
      settingsActions.getPlansInvoices({
        data: scope,
        callback: (props: any) =>
          resolve({
            ...props,
            newPlan: treatRecoveryPlan(response.data),
          }),
      });
    });

    const plans = res.data.map((plan: any) => treatRecoveryPlan(plan));

    if (plans.length === 0 && !out_of_plan) {
      error({
        text: i18nKeys.ERROR.NO_INVOICE_IN_PLAN,
      });
    } else {
      const stepIndex = await new Promise<number | null>(resolve => {
        if (out_of_plan) {
          showDialog({
            id: DialogShowId.CUSTOM,
            size: DialogShowSize.LARGE,
            title: t(i18nKeys.PLAN.MOVE_INVOICES),
            children: <MoveRecoveryPlanInvoiceModal onSubmit={resolve} plan={newPlan} />,
          });
        } else {
          resolve(null);
        }
      });

      const plansSwitch = await new Promise<Array<any>>(resolve => {
        if (plans.length > 0) {
          selectStepPlan(newPlan, plans, 1, plans.length, resolve);
        } else {
          resolve([]);
        }
      });

      if (stepIndex !== null) {
        plansSwitch.push({
          to: planId,
          from: null,
          steps_switch: [{ from: null, to: newPlan.recovery_steps[stepIndex!].id }],
        });
      }

      settingsActions.switchPlansInvoices({
        id: Number(planId),
        data: {
          plans_switch: plansSwitch,
          ...scope,
        },
        callback: ({ data: _data }: any) => {
          showDialog({
            id: DialogShowId.CUSTOM,
            size: DialogShowSize.MEDIUM,
            title: t(i18nKeys.RESULT),
            children: <ListResModal data={_data} />,
          });
        },
      });
    }
  };

  const assignToAccManager = (onSubmit: any) => {
    modals.open({
      size: 'sm',
      styles: { content: { overflow: 'visible' } },
      title: t(i18nKeys.CHOOSE_ACCOUNT_MANAGER),
      children: <ChooseCollaboratorModal onSubmit={onSubmit} />,
    });
  };

  const taskDetails = () => {
    const data: TaskFormProps['batchData'] = {
      filter_params: isSelectedAll ? queryParamParser(qs.parse(location.search)) : undefined,
      view_id: isSelectedAll ? location.hash.slice(1) : undefined,
      debtor_ids: undefined,
      invoice_ids: isSelectedAll ? undefined : selectedInvoices?.map(({ id }) => String(id)),
    };

    modals.open({
      title: t(i18nKeys.FORM.TASK.NEW),
      size: 'xl',
      children: <TaskForm batchData={data} />,
    });
  };

  const customVariablesDetails = (onSubmit: any) => {
    modals.open({
      size: 'sm',
      title: t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.LONG_TITLE),
      children: <CustomVariablesBatchForm onSubmit={onSubmit} resourceType="invoice" />,
    });
  };

  const { default_templates } = company;

  const additionalReminderDetails = (onSubmit: any) => {
    modals.open({
      size: 'sm',
      title: t(i18nKeys.INVOICE.SENDING_OPTIONS),
      children: (
        <SendingOptionsForm
          customText={t(i18nKeys.INVOICE.SENDING_OPTIONS_BATCH_SUMMARY)}
          onSubmit={onSubmit}
          setTemplate={() => {}}
          initialValues={{
            template_id: default_templates.additional_reminder.id,
            send_email: false,
            send_post: false,
          }}
        />
      ),
    });
  };

  const formalNoticeDetails = (onSubmit: any) => {
    modals.open({
      size: 'sm',
      title: t(i18nKeys.INVOICE.SENDING_OPTIONS),
      children: (
        <FormalNoticeForm
          batch
          customText={t(i18nKeys.INVOICE.SENDING_OPTIONS_BATCH_SUMMARY)}
          onSubmit={onSubmit}
          onChangeTemplate={() => {}}
          initialValues={{
            template_id: default_templates.formal_notice.id,
            send_email: true,
          }}
        />
      ),
    });
  };

  const openGcollectFlow = (data: { third_party_company_id: number }) => {
    modals.open({
      title: t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.CONFIRM_RATES),
      centered: true,
      size: 'xl',
      children: (
        <GcollectModal
          gcollectId={data.third_party_company_id}
          invoiceIds={selectedInvoices.map(({ id }) => id)}
        />
      ),
    });
  };

  const openAMLModal = (
    data: { third_party_company_id: number },
    onSubmit: (data: any) => void,
  ) => {
    modals.open({
      title: t(i18nKeys.AML_MODAL.TITLE),
      centered: true,
      size: '70%',
      children: (
        <AmlModal
          invoiceIds={selectedInvoices.map(({ id }) => id)}
          thirdPartyCompanyId={data.third_party_company_id}
          additionalInvoiceIds={[]}
          onSubmit={onSubmit}
        />
      ),
    });
  };

  const openLegalCityModal = (
    data: { third_party_company_id: number },
    _onSubmit: (data: any) => void,
  ) => {
    modals.open({
      title: 'LegalCity',
      centered: true,
      children: (
        <LegalCityModal
          legalcityId={String(data.third_party_company_id)}
          invoiceIds={selectedInvoices.map(({ id }) => id)}
        />
      ),
    });
  };

  const openThirdParty = (
    thirdParties: ThirdParties,
    selectedThirdPartyType: ThirdPartyType,
    onSubmitFunction: Function,
  ) => {
    modals.open({
      size: 'md',
      title: t(i18nKeys.FORM.CHOOSE_THIRD_PARTY_TYPE.TITLE),
      children: (
        <SelectThirdPartyForm
          onSubmit={onSubmitFunction}
          onSubmitGcollect={openGcollectFlow}
          onSubmitLegalCity={openLegalCityModal}
          onSubmitAML={openAMLModal}
          thirdParties={thirdParties}
          selectedThirdPartyType={selectedThirdPartyType}
        />
      ),
    });
  };

  const thirdPartyDetails = (onSubmit: any) => {
    modals.open({
      size: 'lg',
      title: t(i18nKeys.FORM.CHOOSE_THIRD_PARTY_TYPE.TITLE),
      children: (
        <ChooseThirdPartyTypeForm
          invoices={selectedInvoices.map(invoice => ({
            ...omit(invoice, 'attributes'),
            // @ts-expect-error incorrectly typed
            ...invoice.attributes,
          }))}
          onSubmit={(selectedThirdPartyType, thirdParties) =>
            openThirdParty(thirdParties, selectedThirdPartyType, onSubmit)
          }
        />
      ),
    });
  };

  const allActions = [
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_REMINDER,
      description: i18nKeys.BATCH.DESCRIPTIONS.ADDITIONAL_REMINDER,
      function: batchAction('additional_reminder', additionalReminderDetails),
      icon: <IconSend stroke={1.5} size={20} />,
      category: Categories.RECOVERY,
    },
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_FORMAL_NOTICE,
      description: i18nKeys.BATCH.DESCRIPTIONS.FORMAL_NOTICE,
      function: batchAction('formal_notice', formalNoticeDetails),
      icon: <IconFileAlert stroke={1.5} size={20} />,
      category: Categories.RECOVERY,
    },
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_TO_THIRD_PARTY,
      description: i18nKeys.BATCH.DESCRIPTIONS.THIRD_PARTY,
      function: batchAction('third_party', thirdPartyDetails),
      icon: <IconUsers stroke={1.5} size={20} />,
      category: Categories.RECOVERY,
    },
    {
      name: i18nKeys.PAYMENT_PLAN.TITLE.NEW,
      description: i18nKeys.BATCH.DESCRIPTIONS.PAYMENT_PLAN,
      function: paymentPlan,
      icon: <IconCalendar stroke={1.5} size={20} />,
      category: Categories.RECOVERY,
    },
    hasManualActionsActive
      ? {
          name: i18nKeys.MARK_AS_PAID,
          description: i18nKeys.BATCH.DESCRIPTIONS.MARK_AS_PAID,
          function: batchAction('mark_as_paid', aysModal('mark_as_paid')),
          icon: <IconCheck stroke={1.5} size={20} />,
          category: Categories.STATUS,
        }
      : null,
    {
      name: i18nKeys.MARK_AS_DISPUTED,
      description: i18nKeys.BATCH.DESCRIPTIONS.DISPUTE,
      function: dispute,
      icon: <IconAlertCircle stroke={1.5} size={20} />,
      category: Categories.STATUS,
    },
    {
      name: i18nKeys.BATCH.ACTIONS.UNDISPUTE,
      description: i18nKeys.BATCH.DESCRIPTIONS.UNDISPUTE,
      function: batchAction('undispute', aysModal('undispute')),
      icon: <IconCircleCheck stroke={1.5} size={20} />,
      category: Categories.STATUS,
    },
    {
      name: i18nKeys.MARK_AS_UNRECOVERABLE,
      description: i18nKeys.BATCH.DESCRIPTIONS.UNRECOVERABLE,
      function: markAsUnrecoverable,
      icon: <IconBan stroke={1.5} size={20} />,
      category: Categories.STATUS,
    },
    {
      name: i18nKeys.INVOICE.MOVE_PLAN,
      description: i18nKeys.BATCH.DESCRIPTIONS.MOVE_PLAN,
      function: movePlan,
      icon: <IconStack2 stroke={1.5} size={20} />,
      category: Categories.ASSIGNMENT,
    },
    {
      name: i18nKeys.ASSIGN_ACCOUNT_MANAGER_SHORT,
      description: i18nKeys.BATCH.DESCRIPTIONS.ASSIGN,
      function: batchAction('assign', assignToAccManager),
      icon: <IconUser stroke={1.5} size={20} />,
      category: Categories.ASSIGNMENT,
    },
    {
      name: i18nKeys.INVOICE.PAUSE_THE_FOLLOW_UP,
      description: i18nKeys.BATCH.DESCRIPTIONS.PAUSE,
      function: pause,
      icon: <IconPlayerPause stroke={1.5} size={20} />,
      category: Categories.FOLLOW_UP,
    },
    {
      name: i18nKeys.INVOICE.RESUME_FOLLUW_UP,
      description: i18nKeys.BATCH.DESCRIPTIONS.UNPAUSE,
      function: batchAction('unpause', aysModal('unpause')),
      icon: <IconPlayerPlay stroke={1.5} size={20} />,
      category: Categories.FOLLOW_UP,
    },
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.TAKE_OUT_OF_PROCESS,
      description: i18nKeys.BATCH.DESCRIPTIONS.TO_BE_PROCESSED,
      function: batchAction('to_be_processed', aysModal('to_be_processed')),
      icon: <IconLogout stroke={1.5} size={20} />,
      category: Categories.FOLLOW_UP,
    },
    company.package.can_use_tasks
      ? {
          name: i18nKeys.FORM.TASK.NEW,
          description: i18nKeys.BATCH.DESCRIPTIONS.TASK,
          function: taskDetails,
          icon: <IconSquareCheck stroke={1.5} size={20} />,
          category: Categories.ASSIGNMENT,
        }
      : null,
    company.package.can_use_custom_variables
      ? {
          name: i18nKeys.BATCH.ACTIONS.CUSTOM_VARIABLES,
          description: i18nKeys.BATCH.DESCRIPTIONS.CUSTOM_VARIABLES,
          function: batchAction('custom_variables', customVariablesDetails),
          icon: <IconPencil stroke={1.5} size={20} />,
          category: Categories.OTHER,
        }
      : null,
    {
      name: i18nKeys.IGNORE_LATE_FEES,
      description: i18nKeys.BATCH.DESCRIPTIONS.IGNORE_LATE_FEES,
      function: batchAction('ignore_late_fees', aysModal('ignore_late_fees')),
      icon: <IconPercentage stroke={1.5} size={20} />,
      category: Categories.OTHER,
    },
  ];

  const actions = reject(allActions, isNil) as Array<BatchAction>;

  return (
    <div className={styles('batch-actions-wrapper')}>
      <Group gap="xs" mb="md">
        <Badge variant="light" tt="none">
          {t(i18nKeys.BATCH.SELECTED_INVOICES, { count: selectedCount })}
        </Badge>
        {selectedCount < totalObjects && (
          <Anchor component="button" type="button" size="sm" onClick={selectWholeView}>
            {t(i18nKeys.BATCH.SELECT_ALL, { count: totalObjects })}
          </Anchor>
        )}
      </Group>
      <BatchActionsList actions={actions} searchFieldRef={searchFieldRef} />
      <Group justify="space-between" mt="md">
        {hasManualActionsActive && (
          <Button
            color="error"
            leftSection={<IconTrash stroke={1.5} size={18} aria-hidden />}
            onClick={batchAction('delete', aysModal('delete'))}
          >
            {t(i18nKeys.BATCH.DELETE_INVOICES, { count: selectedCount })}
          </Button>
        )}
        <Group gap="md" ml="auto" className={styles('hints')} aria-hidden>
          <Text size="xs" c="dimmed">
            <Kbd size="xs">↑↓</Kbd> {t(i18nKeys.BATCH.HINT_NAVIGATE)}
          </Text>
          <Text size="xs" c="dimmed">
            <Kbd size="xs">↵</Kbd> {t(i18nKeys.BATCH.HINT_CHOOSE)}
          </Text>
          <Text size="xs" c="dimmed">
            <Kbd size="xs">{t(i18nKeys.BATCH.HINT_ESCAPE)}</Kbd> {t(i18nKeys.BATCH.HINT_CLOSE)}
          </Text>
        </Group>
      </Group>
    </div>
  );
}
